import { IconType } from "react-icons";
import { FaSteam } from "react-icons/fa";
import { HiCheck, HiKey, HiLink, HiVideoCamera } from "react-icons/hi";
import { SiEpicgames, SiRiotgames } from "react-icons/si";

import { analytics } from "MVVM/Analytics";
import { useClipSettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import type {
  SetupItem,
  SetupItemKey,
  useSetupChecklist,
} from "MVVM/Hooks/useSetupChecklist";
import {
  MenuAction,
  MenuIcon,
  MenuLink,
  PlatformIcons,
  SetupAction,
  SetupBenefit,
  SetupCard,
  SetupCount,
  SetupDoneSummary,
  SetupFooter,
  SetupHeader,
  SetupHide,
  SetupMeter,
  SetupNextAction,
  SetupRowBlocked,
  SetupRowText,
  SetupRows,
  SetupSegment,
  SetupTitle,
} from "./styledComponents";

type Checklist = ReturnType<typeof useSetupChecklist>;

const ITEM_ICONS: Record<SetupItemKey, IconType> = {
  link: HiLink,
  steambot: FaSteam,
  sharecodes: HiKey,
  autocapture: HiVideoCamera,
};

// Finished steps collapse into one footer line, so these read as receipts.
const DONE_TEXT: Record<SetupItemKey, (item: SetupItem) => string> = {
  link: (item) => item.benefit,
  sharecodes: () => "Share codes added",
  steambot: () => "Steam bot added",
  autocapture: () => "Autocapture on",
};

const RowContent = ({ item, next }: { item: SetupItem; next: boolean }) => {
  const Icon = ITEM_ICONS[item.key];

  return (
    <>
      <MenuIcon>
        <Icon />
      </MenuIcon>
      <SetupRowText>
        {item.label}
        <SetupBenefit>
          {item.key === "link" && (
            <PlatformIcons aria-hidden="true">
              <FaSteam />
              <SiRiotgames />
              <SiEpicgames />
            </PlatformIcons>
          )}
          {item.blockedBy ?? item.benefit}
        </SetupBenefit>
      </SetupRowText>
      {!item.blockedBy &&
        (next ? (
          <SetupNextAction>{item.action}</SetupNextAction>
        ) : (
          <SetupAction>{item.action}</SetupAction>
        ))}
    </>
  );
};

export const SetupChecklist = ({
  checklist,
  onNavigate,
}: {
  checklist: Checklist;
  onNavigate: () => void;
}) => {
  const { open: openClipSettings } = useClipSettingsModal();
  const { items, done, total, dismiss } = checklist;
  const pending = items.filter((item) => !item.done);
  const finished = items.filter((item) => item.done);
  const nextKey = pending.find((item) => !item.blockedBy)?.key;

  const select = (item: SetupItem) => {
    analytics.track("Site Nav - Setup Item Clicked", {
      item: item.key,
      done: item.done,
    });
    onNavigate();
    if (!item.to) openClipSettings();
  };

  const hide = () => {
    analytics.track("Site Nav - Setup Dismissed", { done, total });
    dismiss();
  };

  return (
    <SetupCard aria-labelledby="site-nav-setup-title">
      <SetupHeader>
        <SetupTitle id="site-nav-setup-title">Finish setup</SetupTitle>
        <SetupCount>{total - done} left</SetupCount>
      </SetupHeader>
      <SetupMeter aria-hidden="true">
        {items.map((item, index) => (
          <SetupSegment key={item.key} $filled={index < done} />
        ))}
      </SetupMeter>
      <SetupRows>
        {pending.map((item) => {
          const next = item.key === nextKey;

          return (
            <li key={item.key}>
              {item.blockedBy ? (
                <SetupRowBlocked aria-disabled="true">
                  <RowContent item={item} next={false} />
                </SetupRowBlocked>
              ) : item.to ? (
                <MenuLink to={item.to} onClick={() => select(item)}>
                  <RowContent item={item} next={next} />
                </MenuLink>
              ) : (
                <MenuAction type="button" onClick={() => select(item)}>
                  <RowContent item={item} next={next} />
                </MenuAction>
              )}
            </li>
          );
        })}
      </SetupRows>
      <SetupFooter>
        {finished.length > 0 && (
          <SetupDoneSummary>
            <HiCheck aria-hidden="true" />
            <span>
              {finished.map((item) => DONE_TEXT[item.key](item)).join(" · ")}
            </span>
          </SetupDoneSummary>
        )}
        <SetupHide type="button" onClick={hide}>
          Hide
        </SetupHide>
      </SetupFooter>
    </SetupCard>
  );
};
